/**
 * SRD Composed Mechanic Configs
 *
 * Static composed mechanic JSON configs for SRD patterns that have been
 * migrated from hardcoded components to the Composable Mechanic Engine.
 *
 * Called at app startup from index.js, before React renders.
 *
 * @see composedMechanicRegistry.js
 * @see docs/technical/HOMEBREW_WORKSHOP_DESIGN.md (Phase C)
 */

import { registerComposedMechanics } from './composedMechanicRegistry';

// ─── Helper ─────────────────────────────────────────────────────────────────

const displayOnly = (id, name, summary, containerType) => ({
  id, version: '1.0.0',
  meta: { name, summary, source: 'srd', containerContext: 'ability', containerType, displayOnly: true },
  actions: [],
});

// ─── SUMMARY_ONLY Configs ───────────────────────────────────────────────────

const SUMMARY_ONLY_CONFIGS = [
  // ── Class Features ──
  ['ability:class:Wizard:Prestidigitation',
    displayOnly('srd_wizard_prestidigitation', 'Prestidigitation', 'At will: perform harmless magical effects (change color, light candle, etc.)', 'class')],
  ['ability:class:Warrior:Combat Training',
    displayOnly('srd_warrior_combat_training', 'Combat Training', 'Ignore weapon burden. +Level to physical damage (auto-applied).', 'class')],
  ['ability:class:Warrior:Attack of Opportunity',
    displayOnly('srd_warrior_attack_of_opportunity', 'Attack of Opportunity', 'Reaction: When enemy leaves Melee, roll any trait vs Difficulty. Success: stop them, deal damage, or move with them (pick 2 on crit).', 'class')],
  ['ability:class:Druid:Wildtouch',
    displayOnly('srd_druid_wildtouch', 'Wildtouch', 'Perform harmless nature effects at will (grow flowers, summon breeze, start campfire).', 'class')],

  // ── Subclass Features ──
  ['ability:subclass:Call of the Slayer:Martial Preparation',
    displayOnly('srd_slayer_martial_preparation', 'Martial Preparation', 'Party downtime move: Each participant gains d6 Slayer Die for attack/damage rolls.', 'subclass')],
  ['ability:subclass:Wayfinder:Path Forward',
    displayOnly('srd_wayfinder_path_forward', 'Path Forward', 'Foundation: Identify shortest path to previously visited locations.', 'subclass')],
  ['ability:subclass:School of War:Battlemage',
    displayOnly('srd_school_of_war_battlemage', 'Battlemage', 'Foundation: +1 HP slot. You are trained in martial combat.', 'subclass')],
  ['ability:subclass:School of War:Face Your Fear',
    displayOnly('srd_school_of_war_face_your_fear', 'Face Your Fear', 'When you succeed with Fear on an attack roll, you deal an extra 1d10 magic damage.', 'subclass')],
  ['ability:subclass:School of War:Conjure Shield',
    displayOnly('srd_school_of_war_conjure_shield', 'Conjure Shield', 'Specialization: While you have ≥2 Hope, add Proficiency to Evasion.', 'subclass')],
  ['ability:subclass:School of War:Fueled by Fear',
    displayOnly('srd_school_of_war_fueled_by_fear', 'Fueled by Fear', 'Specialization: Face Your Fear damage increases to 2d10.', 'subclass')],
  ['ability:subclass:School of War:Have No Fear',
    displayOnly('srd_school_of_war_have_no_fear', 'Have No Fear', 'Mastery: Face Your Fear damage increases to 3d10.', 'subclass')],
  ['ability:subclass:Troubadour:Epic Poetry',
    displayOnly('srd_troubadour_epic_poetry', 'Epic Poetry', 'Your Rally Die increases to a d10. When you Help an Ally with narration, roll a d10 as your advantage die.', 'subclass')],
  ['ability:subclass:Syndicate:Well-Connected',
    displayOnly('srd_syndicate_well_connected', 'Well-Connected', 'Mastery: Your network of contacts has expanded. They can now provide more specialized assistance, better information, and larger favors.', 'subclass')],
  ['ability:subclass:Syndicate:Reliable Backup',
    displayOnly('srd_syndicate_reliable_backup', 'Reliable Backup', 'Mastery: Contacts Everywhere 3/session, +2 choices (Shield, Backup).', 'subclass')],
  ['ability:subclass:Divine Wielder:Devout',
    displayOnly('srd_divine_wielder_devout', 'Devout', 'Specialization: Roll extra Prayer Die (discard lowest). Sparing Touch 2x/rest.', 'subclass')],
  ['ability:subclass:Divine Wielder:Sacred Resonance',
    displayOnly('srd_divine_wielder_sacred_resonance', 'Sacred Resonance', 'Spirit Weapon matching dice are doubled (e.g., two 5s = two 10s).', 'subclass')],
  ['ability:subclass:Troubadour:Virtuoso',
    displayOnly('srd_troubadour_virtuoso', 'Virtuoso', 'Mastery: Each song can be performed twice per rest instead of once.', 'subclass')],
  ['ability:subclass:Troubadour:Maestro',
    displayOnly('srd_troubadour_maestro', 'Maestro', 'Specialization: Rally Die ally gains Hope or clears Stress.', 'subclass')],
  ['ability:subclass:Call of the Brave:Courage',
    displayOnly('srd_call_of_the_brave_courage', 'Courage', 'Auto: When you fail with Fear, gain 1 Hope.', 'subclass')],
  ['ability:subclass:Call of the Brave:Rise to the Challenge',
    displayOnly('srd_call_of_the_brave_rise_to_the_challenge', 'Rise to the Challenge', 'While HP ≤ 2: Roll d20 as Hope Die.', 'subclass')],
  ['ability:subclass:Stalwart:Unwavering',
    displayOnly('srd_stalwart_unwavering', 'Unwavering', 'Passive: +1 to all damage thresholds.', 'subclass')],
  ['ability:subclass:Stalwart:Iron Will',
    displayOnly('srd_stalwart_iron_will', 'Iron Will', 'Reactive: On physical damage, mark extra Armor Slot to reduce severity.', 'subclass')],
  ['ability:subclass:Stalwart:Unrelenting',
    displayOnly('srd_stalwart_unrelenting', 'Unrelenting', 'Passive: +2 to all damage thresholds (replaces Unwavering).', 'subclass')],
  ['ability:subclass:Stalwart:Partners-in-Arms',
    displayOnly('srd_stalwart_partners_in_arms', 'Partners-in-Arms', 'Reactive: When ally in Very Close takes damage, mark Armor Slot to reduce severity.', 'subclass')],
  ['ability:subclass:Stalwart:Undaunted',
    displayOnly('srd_stalwart_undaunted', 'Undaunted', 'Passive: +3 to all damage thresholds (replaces Unrelenting).', 'subclass')],
  ['ability:subclass:Vengeance:At Ease',
    displayOnly('srd_vengeance_at_ease', 'At Ease', 'Passive: +1 Stress slot.', 'subclass')],
  ['ability:subclass:Nightwalker:Adrenaline',
    displayOnly('srd_nightwalker_adrenaline', 'Adrenaline', 'Passive: While Vulnerable, add your level to damage rolls.', 'subclass')],
  ['ability:subclass:Nightwalker:Fleeting Shadow',
    displayOnly('srd_nightwalker_fleeting_shadow', 'Fleeting Shadow', 'Passive: +1 Evasion. Shadow Stepper range extends to Very Far.', 'subclass')],
  ['ability:subclass:Winged Sentinel:Ethereal Visage',
    displayOnly('srd_winged_sentinel_ethereal_visage', 'Ethereal Visage', 'Specialization: While flying, advantage on Presence. On Hope success: remove GM Fear instead of gaining Hope.', 'subclass')],
  ['ability:subclass:Winged Sentinel:Ascendant',
    displayOnly('srd_winged_sentinel_ascendant', 'Ascendant', 'Passive: +4 to Severe damage threshold.', 'subclass')],
  ['ability:subclass:Winged Sentinel:Power of the Gods',
    displayOnly('srd_winged_sentinel_power_of_the_gods', 'Power of the Gods', 'Upgrade: Wings of Light bonus damage increased to 1d12 (from 1d8).', 'subclass')],
  ['ability:subclass:Warden of Renewal:Regenerative Reach',
    displayOnly('srd_warden_of_renewal_regenerative_reach', 'Regenerative Reach', 'Upgrade: Regeneration now works at Very Close range (not just Touch).', 'subclass')],
  ['ability:subclass:Warden of the Elements:Elemental Dominion',
    displayOnly('srd_warden_of_the_elements_elemental_dominion', 'Elemental Dominion', 'While Channeling, gain element benefit: Fire +1 Prof, Earth d6→reduce HP, Water Stress→Vulnerable, Air +1 Evasion+fly.', 'subclass')],

  // ── Ancestry Features ──
  ['ability:ancestry:Simiah:Nimble',
    displayOnly('srd_simiah_nimble', 'Nimble', "Climbing doesn't cost extra movement. +1 on rolls to grab/hold.", 'ancestry')],
  ['ability:ancestry:Clank:Efficient',
    displayOnly('srd_clank_efficient', 'Efficient', "Short rests take half the time. Don't need food/water/sleep.", 'ancestry')],
  ['ability:ancestry:Elf:Celestial Trance',
    displayOnly('srd_elf_celestial_trance', 'Celestial Trance', 'During a rest, drop into trance to choose an additional downtime move.', 'ancestry')],
  ['ability:ancestry:Faun:Caprine Leap',
    displayOnly('srd_faun_caprine_leap', 'Caprine Leap', 'Leap anywhere within Close range as normal movement.', 'ancestry')],
  ['ability:ancestry:Giant:Reach',
    displayOnly('srd_giant_reach', 'Reach', 'Treat Melee range as Very Close for all weapons and abilities.', 'ancestry')],
  ['ability:ancestry:Ribbet:Amphibious',
    displayOnly('srd_ribbet_amphibious', 'Amphibious', 'Breathe and move naturally underwater.', 'ancestry')],
  ['ability:ancestry:Simiah:Natural Climber',
    displayOnly('srd_simiah_natural_climber', 'Natural Climber', 'Advantage on Agility Rolls for balancing and climbing.', 'ancestry')],
  ['ability:ancestry:Infernis:Dread Visage',
    displayOnly('srd_infernis_dread_visage', 'Dread Visage', 'Advantage on rolls to intimidate hostile creatures.', 'ancestry')],
  ['ability:ancestry:Goblin:Surefooted',
    displayOnly('srd_goblin_surefooted', 'Surefooted', 'Ignore disadvantage on Agility Rolls.', 'ancestry')],
  ['ability:ancestry:Orc:Sturdy',
    displayOnly('srd_orc_sturdy', 'Sturdy', 'At 1 HP remaining, attacks against you have disadvantage.', 'ancestry')],
  ['ability:ancestry:Fungril:Fungril Network',
    displayOnly('srd_fungril_fungril_network', 'Fungril Network', 'Instinct Roll (12) to communicate with other fungril across any distance.', 'ancestry')],
  ['ability:ancestry:Firbolg:Unshakable',
    displayOnly('srd_firbolg_unshakable', 'Unshakable', "When marking Stress, roll d6. On 6, don't mark it.", 'ancestry')],
  ['ability:ancestry:Galapa:Shell',
    displayOnly('srd_galapa_shell', 'Shell', 'Gain +Proficiency to all damage thresholds.', 'ancestry')],
  ['ability:ancestry:Giant:Endurance',
    displayOnly('srd_giant_endurance', 'Endurance', 'Gain +1 Hit Point slot at character creation.', 'ancestry')],
  ['ability:ancestry:Halfling:Internal Compass',
    displayOnly('srd_halfling_internal_compass', 'Internal Compass', 'When you roll a 1 on your Hope Die, you can reroll it.', 'ancestry')],
  ['ability:ancestry:Human:High Stamina',
    displayOnly('srd_human_high_stamina', 'High Stamina', 'Gain +1 Stress slot at character creation.', 'ancestry')],
  ['ability:ancestry:Firbolg:Charge',
    displayOnly('srd_firbolg_charge', 'Charge', 'After Agility success (movement to Melee), option to mark 1 Stress for 1d12 AoE damage appears in roll results.', 'ancestry')],
  ['ability:ancestry:Infernis:Fearless',
    displayOnly('srd_infernis_fearless', 'Fearless', 'On Fear result, option to mark 2 Stress and convert to Hope appears in roll results.', 'ancestry')],

  // ── Community Features ──
  ['ability:community:Highborne:Privilege',
    displayOnly('srd_highborne_privilege', 'Privilege', 'Advantage on Presence rolls involving nobility or high society.', 'community')],
  ['ability:community:Loreborne:Well Read',
    displayOnly('srd_loreborne_well_read', 'Well Read', 'Advantage on Knowledge rolls about history, culture, or politics.', 'community')],
  ['ability:community:Ridgeborne:Steady',
    displayOnly('srd_ridgeborne_steady', 'Steady', 'Advantage on Agility rolls for traversal and survival.', 'community')],
  ['ability:community:Slyborne:Scoundrel',
    displayOnly('srd_slyborne_scoundrel', 'Scoundrel', 'Advantage on Finesse or Presence rolls for criminal/deception activities.', 'community')],
  ['ability:community:Underborne:Low Light Living',
    displayOnly('srd_underborne_low_light_living', 'Low Light Living', 'Advantage on rolls in low light or shadow areas.', 'community')],
  ['ability:community:Wildborne:Lightfoot',
    displayOnly('srd_wildborne_lightfoot', 'Lightfoot', 'Advantage on Agility or Finesse rolls for silent movement.', 'community')],
  ['ability:community:Seaborne:Know the Tide',
    displayOnly('srd_seaborne_know_the_tide', 'Know the Tide', 'Gain Tide tokens on Fear rolls. Spend tokens for +1 per token on action rolls.', 'community')],
];

// ─── ACTIVATION_TOGGLE Configs ──────────────────────────────────────────────

const toggle = (id, name, summary, containerType, flag, hopeCost, labels, meta = {}) => {
  const conditions = [{ type: 'not_has_character_flag', flag }];
  const costs = [];
  if (hopeCost > 0) {
    conditions.unshift({ type: 'resource_available', resource: 'hope', amount: hopeCost });
    costs.push({ type: 'spend_hope', amount: hopeCost });
  }
  return {
    id, version: '1.0.0',
    meta: { name, summary, source: 'srd', containerContext: 'ability', containerType, ...meta },
    actions: [{
      id: 'toggle', label: labels.activate, variant: 'primary',
      conditions, costs,
      effects: [
        { type: 'set_character_flag', flag, value: true },
        { type: 'show_toast', message: labels.activateMsg },
      ],
      toggleOff: {
        label: labels.active, variant: 'activeSuccess',
        effects: [
          { type: 'clear_character_flag', flag },
          { type: 'show_toast', message: labels.deactivateMsg },
        ],
      },
    }],
  };
};

const ACTIVATION_TOGGLE_CONFIGS = [
  // ── Class Features ──
  ["ability:class:Ranger:Ranger's Focus",
    toggle('srd_ranger_rangers_focus', "Ranger's Focus",
      'Mark a target. Gain advantage on Finesse rolls against them.', 'class',
      'rangersFocusActive', 1, {
        activate: '🎯 Focus', active: '✓ Focused',
        activateMsg: "🎯 Ranger's Focus active - advantage on next Finesse roll!",
        deactivateMsg: "🎯 Ranger's Focus deactivated",
      })],
  ['ability:class:Rogue:Cloaked',
    toggle('srd_rogue_cloaked', 'Cloaked',
      'While Cloaked: unseen if stationary when spotted. Enables Sneak Attack.', 'class',
      'isCloaked', 0, {
        activate: '🥷 Hide (Cloaked)', active: '✓ Cloaked',
        activateMsg: '🥷 You are Cloaked! Remain unseen while stationary.',
        deactivateMsg: '🥷 Cloaked ended',
      })],

  // ── Subclass Features ──
  ['ability:subclass:Beastbound:Battle-Bonded',
    toggle('srd_beastbound_battle_bonded', 'Battle-Bonded',
      '+2 Evasion when companion in melee range of attacker', 'subclass',
      'battleBondedActive', 0, {
        activate: 'Companion in Melee', active: '✓ Active',
        activateMsg: 'Battle-Bonded: +2 Evasion while companion in melee!',
        deactivateMsg: 'Battle-Bonded: Companion left melee range',
      }, { hideActiveIndicator: true })],
  ['ability:subclass:Wayfinder:Elusive Predator',
    toggle('srd_wayfinder_elusive_predator', 'Elusive Predator',
      'Specialization: +2 Evasion when Focus attacks you', 'subclass',
      'elusivePredatorActive', 0, {
        activate: '🎯 Focus Attacks', active: '✓ Evading Focus',
        activateMsg: '🎯 Elusive Predator: +2 Evasion vs Focus target!',
        deactivateMsg: 'Elusive Predator: Focus not attacking you',
      })],
  ['ability:subclass:Divine Wielder:Spirit Weapon',
    toggle('srd_divine_wielder_spirit_weapon', 'Spirit Weapon',
      'Toggle: Melee weapon attacks at Close range', 'subclass',
      'spiritWeaponRanged', 0, {
        activate: '✨ Throw Weapon', active: '✓ Ranged Mode',
        activateMsg: 'Spirit Weapon: Attack at Close range! Mark Stress for extra target.',
        deactivateMsg: 'Spirit Weapon: Returned to melee',
      })],
  ['ability:subclass:Vengeance:Act of Reprisal',
    toggle('srd_vengeance_act_of_reprisal', 'Act of Reprisal',
      'When ally in Melee takes damage: Toggle for +1 Prof on next attack vs attacker', 'subclass',
      'actOfReprisalActive', 0, {
        activate: '💢 Track Attacker', active: '💢 +1 Prof Ready',
        activateMsg: 'Act of Reprisal: +1 Proficiency on your next attack vs this enemy!',
        deactivateMsg: 'Act of Reprisal: Bonus applied to attack',
      })],
  ['ability:subclass:Winged Sentinel:Wings of Light',
    toggle('srd_winged_sentinel_wings_of_light', 'Wings of Light',
      'Toggle flight. While flying: Mark Stress to carry ally, Spend 1 Hope for +1d8 damage.', 'subclass',
      'isFlying', 0, {
        activate: '🪽 Take Flight', active: '🪽 Flying',
        activateMsg: 'Wings of Light: Now flying! Spend 1 Hope for bonus damage on attacks.',
        deactivateMsg: 'Wings of Light: Landed',
      })],
  ['ability:subclass:Primal Origin:Arcane Charge',
    toggle('srd_primal_origin_arcane_charge', 'Arcane Charge',
      'Spend 2 Hope (or take magic dmg) to charge. Discharge for +10 dmg or +3 Difficulty.', 'subclass',
      'arcaneCharged', 2, {
        activate: '⚡ Charge (2 Hope)', active: '⚡ CHARGED',
        activateMsg: 'Arcane Charge: CHARGED! Bonus on next magic attack.',
        deactivateMsg: 'Arcane Charge: Discharged (+10 dmg or +3 Difficulty)',
      })],
];

// ─── HOPE_SPEND + HOPE_SPEND_REROLL Configs ────────────────────────────────

const hopeSpend = (id, name, summary, containerType, hopeCost, buttonLabel, icon, charFlag, flagValue) => {
  const conditions = [{ type: 'resource_available', resource: 'hope', amount: hopeCost }];
  const effects = [{ type: 'show_toast', message: `${name} activated!`, icon: icon || '✨' }];
  const labels = { disabled: `Need ${hopeCost} Hope` };

  if (charFlag) {
    conditions.push({ type: 'not_has_character_flag', flag: charFlag });
    effects.unshift({ type: 'set_character_flag', flag: charFlag, value: flagValue !== undefined ? flagValue : true });
    labels.active = '✓ Active';
  }

  return {
    id, version: '1.0.0',
    meta: { name, summary, source: 'srd', containerContext: 'ability', containerType },
    actions: [{
      id: 'activate', label: buttonLabel, variant: 'primary',
      conditions,
      costs: [{ type: 'spend_hope', amount: hopeCost }],
      effects,
      labels,
    }],
  };
};

const stressForBonus = (id, name, summary, containerType, stressCost, buttonLabel, icon, effectMessage, opts = {}) => {
  const effects = [{ type: 'show_toast', message: effectMessage, icon: icon || '✨' }];
  if (opts.sessionStateKey) {
    effects.push({ type: 'set_session_state', key: opts.sessionStateKey, value: true });
  }
  return {
    id, version: '1.0.0',
    meta: { name, summary, source: 'srd', containerContext: 'ability', containerType },
    actions: [{
      id: 'activate', label: buttonLabel, variant: 'warning',
      conditions: [{ type: 'resource_available', resource: 'stress', amount: stressCost }],
      costs: [{ type: 'mark_stress', amount: stressCost }],
      effects,
      labels: { disabled: stressCost > 1 ? `Need ${stressCost} Stress capacity` : 'No Stress capacity' },
      ...(opts.autoClear ? { autoClear: opts.autoClear } : {}),
    }],
  };
};

const domainCard = (cardKey, name, domain, level, cardType, summary, opts = {}) => {
  const { cost, buttonLabel, buttonIcon, sessionStateKey, activeLabel, toastMsg } = opts;
  const conditions = [];
  const costs = [];
  const effects = [];
  if (cost?.type === 'stress') {
    conditions.push({ type: 'resource_available', resource: 'stress', amount: cost.amount });
    costs.push({ type: 'mark_stress', amount: cost.amount });
  } else if (cost?.type === 'hope') {
    conditions.push({ type: 'resource_available', resource: 'hope', amount: cost.amount });
    costs.push({ type: 'spend_hope', amount: cost.amount });
  }
  effects.push({ type: 'show_toast', message: toastMsg || `${name} activated!`, icon: buttonIcon || '✨' });
  if (sessionStateKey) {
    effects.push({ type: 'set_session_state', key: sessionStateKey, value: true });
  }
  const labels = {};
  if (activeLabel) labels.active = activeLabel;
  if (cost?.type === 'stress') {
    labels.disabled = cost.amount > 1 ? `Need ${cost.amount} Stress` : 'No Stress';
  } else if (cost?.type === 'hope') {
    labels.disabled = cost.amount > 1 ? `Need ${cost.amount} Hope` : 'Need 1 Hope';
  }
  return [cardKey, {
    id: `srd_dc_${cardKey.replace(/-/g, '_')}`,
    version: '1.0.0',
    meta: {
      name, summary, description: summary, source: 'srd',
      containerContext: 'domain-card', domain, level, cardType,
    },
    actions: [{
      id: 'activate',
      label: buttonLabel || name,
      variant: cost?.type === 'stress' ? 'warning' : 'primary',
      effects,
      ...(conditions.length ? { conditions } : {}),
      ...(costs.length ? { costs } : {}),
      ...(Object.keys(labels).length ? { labels } : {}),
    }],
  }];
};

const domainCardToken = (cardKey, name, domain, level, cardType, summary, tokenId, refreshOn, opts = {}) => {
  const { cost, buttonLabel, buttonIcon, sessionStateKey, activeLabel, toastMsg } = opts;
  const conditions = [{ type: 'has_token', tokenId }];
  const costs = [{ type: 'spend_token', tokenId, amount: 1 }];
  const effects = [];
  if (cost?.type === 'stress') {
    conditions.unshift({ type: 'resource_available', resource: 'stress', amount: cost.amount });
    costs.unshift({ type: 'mark_stress', amount: cost.amount });
  } else if (cost?.type === 'hope') {
    conditions.unshift({ type: 'resource_available', resource: 'hope', amount: cost.amount });
    costs.unshift({ type: 'spend_hope', amount: cost.amount });
  }
  effects.push({ type: 'show_toast', message: toastMsg || `${name} activated!`, icon: buttonIcon || '✨' });
  if (sessionStateKey) {
    effects.push({ type: 'set_session_state', key: sessionStateKey, value: true });
  }
  const labels = {};
  if (activeLabel) labels.active = activeLabel;
  const tokenMsg = refreshOn === 'long-rest' ? 'Used This Long Rest' : 'Used This Rest';
  if (cost) {
    const disabledObj = { default: tokenMsg };
    if (cost.type === 'stress') disabledObj.noStress = cost.amount > 1 ? `Need ${cost.amount} Stress` : 'No Stress';
    if (cost.type === 'hope') disabledObj.noHope = cost.amount > 1 ? `Need ${cost.amount} Hope` : 'Need 1 Hope';
    labels.disabled = disabledObj;
  } else {
    labels.disabled = tokenMsg;
  }
  return [cardKey, {
    id: `srd_dc_${cardKey.replace(/-/g, '_')}`,
    version: '1.0.0',
    meta: {
      name, summary, description: summary, source: 'srd',
      containerContext: 'domain-card', domain, level, cardType,
    },
    actions: [{
      id: 'activate',
      label: buttonLabel || name,
      variant: cost?.type === 'stress' ? 'warning' : 'primary',
      conditions, costs, effects, labels,
    }],
  }];
};

const HOPE_SPEND_CONFIGS = [
  // ── Hope Features (containerType: 'hope') ──
  ['ability:hope:Sorcerer:Volatile Magic',
    hopeSpend('srd_sorcerer_volatile_magic', 'Volatile Magic',
      'Spend 3 Hope to reroll any damage dice on a magic attack', 'hope',
      3, '💥 Volatile Cast', '💥')],
  ['ability:hope:Ranger:Hold Them Off',
    hopeSpend('srd_ranger_hold_them_off', 'Hold Them Off',
      'Spend 3 Hope to use successful attack roll on 2 more targets', 'hope',
      3, '🏹 Hold Them Off', '🏹')],
  ['ability:hope:Guardian:Frontline Tank',
    hopeSpend('srd_guardian_frontline_tank', 'Frontline Tank',
      'Spend 3 Hope to clear 2 Armor Slots', 'hope',
      3, '🛡️ Clear Armor', '🛡️')],
  ['ability:hope:Warrior:No Mercy',
    hopeSpend('srd_warrior_no_mercy', 'No Mercy',
      'Spend 3 Hope for +1 to attack rolls until rest', 'hope',
      3, '⚔️ No Mercy', '⚔️', 'noMercyBonus', 1)],
  ["ability:hope:Rogue:Rogue's Dodge",
    hopeSpend('srd_rogue_rogues_dodge', "Rogue's Dodge",
      'Spend 3 Hope: +2 Evasion until hit or rest (auto-clears)', 'hope',
      3, "🗡️ Rogue's Dodge", '🗡️', 'roguesDodgeActive')],
  ['ability:hope:Bard:Make a Scene',
    hopeSpend('srd_bard_make_a_scene', 'Make a Scene',
      'Spend 3 Hope: Target within Close range is Distracted (-2 Difficulty)', 'hope',
      3, '🎭 Make a Scene', '🎭')],

  // ── Hope Features (containerType: 'hope') — Reroll variants ──
  ['ability:hope:Wizard:Not This Time',
    hopeSpend('srd_wizard_not_this_time', 'Not This Time',
      'Spend 3 Hope to force adversary to reroll attack or damage roll.', 'hope',
      3, '🛡️ Force Reroll', '🛡️')],

  // ── Subclass Features ──
  ['ability:subclass:Wayfinder:Apex Predator',
    hopeSpend('srd_wayfinder_apex_predator', 'Apex Predator',
      'Spend Hope before attack vs Focus to remove GM Fear on success.', 'subclass',
      1, 'Apex Strike (vs Focus)', '🐺')],
  ['ability:subclass:Wordsmith:Heart of a Poet',
    hopeSpend('srd_wordsmith_heart_of_a_poet', 'Heart of a Poet',
      'After action roll to impress/persuade/offend: Spend 1 Hope to add d4', 'subclass',
      1, '📝 Add d4', '📝')],
  ['ability:subclass:Vengeance:Nemesis',
    hopeSpend('srd_vengeance_nemesis', 'Nemesis',
      'Spend 2 Hope: Prioritize adversary until rest. Swap Hope/Fear dice vs them.', 'subclass',
      2, '🎯 Prioritize (2 Hope)', '🎯', 'nemesisTarget')],
  ['ability:subclass:Warden of Renewal:Regeneration',
    hopeSpend('srd_warden_of_renewal_regeneration', 'Regeneration',
      'Touch creature: Spend 3 Hope, they clear 1d4 HP', 'subclass',
      3, '💚 Regenerate (3 Hope)', '💚')],

  // ── Ancestry Features ──
  ['ability:ancestry:Dwarf:Increased Fortitude',
    hopeSpend('srd_dwarf_increased_fortitude', 'Increased Fortitude',
      'Spend 3 Hope to halve incoming physical damage.', 'ancestry',
      3, '💪 Fortitude', '💪')],
  ['ability:ancestry:Katari:Feline Instincts',
    hopeSpend('srd_katari_feline_instincts', 'Feline Instincts',
      'On Agility Roll, spend 2 Hope to reroll your Hope Die.', 'ancestry',
      2, '🐱 Feline Instincts', '🐱')],
];

// ─── STRESS_FOR_BONUS Configs ────────────────────────────────────────────────

const STRESS_FOR_BONUS_CONFIGS = [
  // ── Subclass Features ──
  ['ability:subclass:Wayfinder:Ruthless Predator',
    stressForBonus('srd_wayfinder_ruthless_predator', 'Ruthless Predator',
      'Mark Stress for +1 Proficiency on damage. Severe damage forces enemy Stress.', 'subclass',
      1, '🎯 +1 Proficiency', '🎯', '+1 Proficiency on this damage roll!')],
  ['ability:subclass:Wayfinder:Adept',
    stressForBonus('srd_wayfinder_adept', 'Adept',
      'Mark Stress to double Experience modifier.', 'subclass',
      1, '📚 Double Experience', '📚', 'Experience modifier doubled! (+4 instead of +2)')],
  ['ability:subclass:Wayfinder:Thrive in Chaos',
    stressForBonus('srd_wayfinder_thrive_in_chaos', 'Thrive in Chaos',
      'After attack success, mark Stress to force +1 HP on target.', 'subclass',
      1, '⚡ +1 HP to Target', '⚡', 'Target marks an additional Hit Point!')],
  ['ability:subclass:Stalwart:Loyal Protector',
    stressForBonus('srd_stalwart_loyal_protector', 'Loyal Protector',
      'When ally in Close (HP ≤ 2) would take damage: Mark Stress to take it instead', 'subclass',
      1, '🛡️ Intercept', '🛡️', 'Sprinting to ally! Taking their damage instead.')],
  ['ability:subclass:Nightwalker:Shadow Stepper',
    stressForBonus('srd_nightwalker_shadow_stepper', 'Shadow Stepper',
      'Mark Stress: Teleport to shadow within Far range + become Cloaked', 'subclass',
      1, '🌑 Shadow Step', '🌑', 'Teleported to shadow! You are Cloaked.')],
  ['ability:subclass:Nightwalker:Vanishing Act',
    stressForBonus('srd_nightwalker_vanishing_act', 'Vanishing Act',
      'Mark Stress: Become Cloaked anytime + clear Restrained', 'subclass',
      1, '👻 Vanish', '👻', 'You vanished! Cloaked until Fear roll or rest.')],
  ['ability:subclass:Vengeance:Revenge',
    stressForBonus('srd_vengeance_revenge', 'Revenge',
      'Reactive: When hit in Melee, mark 2 Stress to force attacker to mark 1 HP', 'subclass',
      2, '⚔️ Revenge!', '⚔️', 'Attacker marks 1 HP!')],
  ['ability:subclass:Warden of Renewal:Defender',
    stressForBonus('srd_warden_of_renewal_defender', 'Defender',
      'Beastform only: When ally in Close marks 2+ HP, mark Stress to reduce by 1', 'subclass',
      1, '🐻 Defend Ally', '🐻', 'Ally marks 1 fewer HP!')],

  // ── Ancestry Features ──
  ['ability:ancestry:Faerie:Wings',
    stressForBonus('srd_faerie_wings', 'Wings',
      'You can fly. After being attacked, mark Stress for +2 Evasion against that attack.', 'ancestry',
      1, '🧚 Wings', '🧚', '+2 Evasion against this attack!',
      { sessionStateKey: 'wingsActive', autoClear: 3000 })],
  ['ability:ancestry:Fungril:Death Connection',
    stressForBonus('srd_fungril_death_connection', 'Death Connection',
      'Touch a recent corpse and mark Stress to extract one memory.', 'ancestry',
      1, '💀 Death Connection', '💀', 'Extracting memory from corpse...',
      { sessionStateKey: 'deathConnectionActive', autoClear: 3000 })],
  ['ability:ancestry:Human:Adaptability',
    stressForBonus('srd_human_adaptability', 'Adaptability',
      'On failed Experience roll, mark Stress to reroll.', 'ancestry',
      1, '🔄 Adapt', '🔄', 'Rerolling failed Experience roll!',
      { sessionStateKey: 'adaptabilityActive', autoClear: 3000 })],
];

// ─── DOMAIN_CARD SingleAction Configs ────────────────────────────────────────

const DOMAIN_CARD_SINGLE_ACTION_CONFIGS = [
  // ── BLADE Domain ──
  domainCard('not-good-enough', 'Not Good Enough', 'blade', 1, 'Ability',
    'When rolling damage: Reroll any 1s or 2s. FREE - no cost!',
    { buttonLabel: '🎲 Reroll 1s/2s', buttonIcon: '🎲',
      toastMsg: 'Remember: Reroll any 1s or 2s on damage!' }),
  domainCard('whirlwind', 'Whirlwind', 'blade', 1, 'Ability',
    'After successful melee attack: Spend Hope to hit all other Very Close targets (half damage).',
    { cost: { type: 'hope', amount: 1 }, buttonLabel: '🌀 Whirlwind (1H)', buttonIcon: '🌀',
      sessionStateKey: 'whirlwindReady', activeLabel: '✓ AoE Ready' }),
  domainCard('get-back-up', 'Get Back Up', 'blade', 1, 'Reaction',
    'On Severe damage: Mark Stress to reduce severity by one threshold.',
    { cost: { type: 'stress', amount: 1 }, buttonLabel: '💪 Get Back Up (1S)', buttonIcon: '💪' }),
  domainCard('reckless', 'Reckless', 'blade', 2, 'Ability',
    'Mark Stress to gain advantage on an attack.',
    { cost: { type: 'stress', amount: 1 }, buttonLabel: '⚔️ Reckless (1S)', buttonIcon: '⚔️',
      sessionStateKey: 'recklessAdvantage', activeLabel: 'Advantage Ready' }),
  domainCard('versatile-fighter', 'Versatile Fighter', 'blade', 3, 'Ability',
    'Passive: Use any trait for weapon. Active: Mark Stress to use max result on one damage die.',
    { cost: { type: 'stress', amount: 1 }, buttonLabel: '🎲 Max Damage Die (1S)', buttonIcon: '🎲',
      sessionStateKey: 'versatileFighterMaxDamage', activeLabel: 'Max Damage Ready' }),
  domainCard('glancing-blow', 'Glancing Blow', 'blade', 7, 'Reaction',
    'On FAILED attack: Mark Stress to deal weapon damage using HALF your Proficiency.',
    { cost: { type: 'stress', amount: 1 }, buttonLabel: '⚔️ Glancing Blow (1S)', buttonIcon: '⚔️' }),
  domainCard('onslaught', 'Onslaught', 'blade', 10, 'Ability',
    'Passive: Min Major damage. Reactive: When ally attacked, 1 Stress forces enemy Reaction (15) or mark HP.',
    { cost: { type: 'stress', amount: 1 }, buttonLabel: '⚔️ Force Reaction (1S)', buttonIcon: '⚔️' }),
  domainCard('battle-monster', 'Battle Monster', 'blade', 10, 'Ability',
    'On successful attack: Mark 4 Stress → target marks HP equal to YOUR current marked HP!',
    { cost: { type: 'stress', amount: 4 }, buttonLabel: '💀 Battle Monster (4S)', buttonIcon: '💀' }),

  // ── BONE Domain ──
  domainCard('ferocity', 'Ferocity', 'bone', 2, 'Ability',
    'After enemy marks HP: Spend 2 Hope for +Evasion = HP marked (until next attack vs you).',
    { cost: { type: 'hope', amount: 2 }, buttonLabel: '🔥 Ferocity (2H)', buttonIcon: '🔥',
      sessionStateKey: 'ferocityBonus', activeLabel: '✓ +Evasion Active' }),
  domainCard('brace', 'Brace', 'bone', 3, 'Ability',
    'When marking Armor Slot, mark Stress to mark an additional Armor Slot.',
    { cost: { type: 'stress', amount: 1 }, buttonLabel: '🛡️ Brace (1S)', buttonIcon: '🛡️' }),
  ['tactician', {
    id: 'srd_dc_tactician', version: '1.0.0',
    meta: {
      name: 'Tactician',
      summary: 'Help an Ally: they spend Hope for +Experience. Tag Team: d20 as Hope Die.',
      description: 'Help an Ally: they spend Hope for +Experience. Tag Team: d20 as Hope Die.',
      source: 'srd', containerContext: 'domain-card',
      domain: 'bone', level: 3, cardType: 'Ability', displayOnly: true,
    },
    actions: [],
  }],
  domainCard('boost', 'Boost', 'bone', 4, 'Ability',
    'Mark Stress: boost off ally, aerial attack vs Far target. Advantage + d10 damage.',
    { cost: { type: 'stress', amount: 1 }, buttonLabel: '🚀 Boost Attack (1S)', buttonIcon: '🚀',
      sessionStateKey: 'boostActive', activeLabel: 'Aerial Attack Ready' }),
  domainCard('redirect', 'Redirect', 'bone', 4, 'Reaction',
    'On failed ranged attack vs you: Roll Prof×d6. On any 6, redirect to nearby enemy.',
    { cost: { type: 'stress', amount: 1 }, buttonLabel: '🔄 Redirect (1S)', buttonIcon: '🔄' }),
  domainCard('recovery', 'Recovery', 'bone', 6, 'Ability',
    'During short rest: You get long rest move. Spend Hope for ally to do same.',
    { cost: { type: 'hope', amount: 1 }, buttonLabel: '🏕️ Ally Long Rest Move (1H)', buttonIcon: '🏕️' }),
  domainCard('rapid-riposte', 'Rapid Riposte', 'bone', 6, 'Reaction',
    'On failed Melee attack vs you: Mark Stress to deal weapon damage to attacker.',
    { cost: { type: 'stress', amount: 1 }, buttonLabel: '⚔️ Riposte (1S)', buttonIcon: '⚔️' }),
  domainCard('breaking-blow', 'Breaking Blow', 'bone', 8, 'Ability',
    'After successful attack: Mark Stress. NEXT attack vs same target deals +2d12 damage!',
    { cost: { type: 'stress', amount: 1 }, buttonLabel: '💥 Breaking Blow (1S)', buttonIcon: '💥',
      sessionStateKey: 'breakingBlowActive', activeLabel: '💥 +2d12 Ready!' }),

  // ── MIDNIGHT Domain ──
  domainCard('chokehold', 'Chokehold', 'midnight', 3, 'Ability',
    'Mark Stress to put target in chokehold. They become Vulnerable (+2d6 damage from attacks).',
    { cost: { type: 'stress', amount: 1 }, buttonLabel: '🤜 Chokehold (1S)', buttonIcon: '🤜' }),
  domainCard('stealth-expertise', 'Stealth Expertise', 'midnight', 4, 'Ability',
    'When rolling Fear on stealth, mark Stress to roll with Hope instead (self or Close ally).',
    { cost: { type: 'stress', amount: 1 }, buttonLabel: '🤫 Convert Fear to Hope (1S)', buttonIcon: '🤫' }),
  domainCard('vanishing-dodge', 'Vanishing Dodge', 'midnight', 7, 'Spell',
    'When physical attack fails vs you: Spend Hope to become Hidden + teleport Close to attacker.',
    { cost: { type: 'hope', amount: 1 }, buttonLabel: '👻 Vanishing Dodge (1H)', buttonIcon: '👻',
      sessionStateKey: 'vanishingDodgeHidden', activeLabel: '✓ Hidden!' }),

  // ── ARCANA Domain ──
  domainCard('wall-walk', 'Wall Walk', 'arcana', 1, 'Spell',
    'Spend Hope to let creature walk on walls/ceilings until end of scene or recast.',
    { cost: { type: 'hope', amount: 1 }, buttonLabel: '🕷️ Wall Walk (1H)', buttonIcon: '🕷️',
      sessionStateKey: 'wallWalkActive', activeLabel: '✓ Wall Walk Active' }),
  domainCard('floating-eye', 'Floating Eye', 'arcana', 2, 'Spell',
    'Spend Hope to create scouting orb (Very Far range). Ends if damaged or out of range.',
    { cost: { type: 'hope', amount: 1 }, buttonLabel: '👁️ Floating Eye (1H)', buttonIcon: '👁️',
      sessionStateKey: 'floatingEyeActive', activeLabel: '✓ Eye Active' }),
  domainCard('cloaking-blast', 'Cloaking Blast', 'arcana', 7, 'Spell',
    'After successful Spellcast: Spend Hope to become Cloaked (unseen while stationary).',
    { cost: { type: 'hope', amount: 1 }, buttonLabel: '🫥 Cloak (1H)', buttonIcon: '🫥',
      sessionStateKey: 'cloakingBlastActive', activeLabel: '✓ Cloaked' }),
  domainCard('arcane-reflection', 'Arcane Reflection', 'arcana', 8, 'Reaction',
    'On magic damage: Spend Hope → roll d6s. Any 6 reflects attack to caster!',
    { buttonLabel: '✨ Reflect Magic', buttonIcon: '✨',
      toastMsg: 'Arcane Reflection! Spend Hope, roll d6s. Any 6 reflects the attack!' }),

  // ── GRACE Domain ──
  domainCard('deft-deceiver', 'Deft Deceiver', 'grace', 1, 'Ability',
    'Spend Hope to gain advantage on deception/trick roll.',
    { cost: { type: 'hope', amount: 1 }, buttonLabel: '🎭 Deceive (1H)', buttonIcon: '🎭',
      sessionStateKey: 'deftDeceiverActive', activeLabel: '✓ Advantage Ready' }),
  domainCard('endless-charisma', 'Endless Charisma', 'grace', 7, 'Ability',
    'After persuade/lie/favor roll: Spend Hope to reroll Hope or Fear Die.',
    { cost: { type: 'hope', amount: 1 }, buttonLabel: '✨ Reroll Die (1H)', buttonIcon: '✨',
      sessionStateKey: 'endlessCharismaReady', activeLabel: '✓ Reroll Ready' }),

  // ── SPLENDOR Domain ──
  domainCard('shape-material', 'Shape Material', 'splendor', 5, 'Spell',
    'Spend Hope to shape natural material (stone, ice, wood) within Close range.',
    { cost: { type: 'hope', amount: 1 }, buttonLabel: '🪨 Shape Material (1H)', buttonIcon: '🪨' }),

  // ── VALOR Domain ──
  domainCard('support-tank', 'Support Tank', 'valor', 4, 'Ability',
    'When ally in Close range fails roll: Spend 2 Hope to let them reroll Hope or Fear Die.',
    { cost: { type: 'hope', amount: 2 }, buttonLabel: '🛡️ Support Tank (2H)', buttonIcon: '🛡️' }),
  domainCard('shrug-it-off', 'Shrug It Off', 'valor', 7, 'Reaction',
    'On damage: Mark Stress to reduce severity. Roll d6 — on ≤3, card vaults.',
    { cost: { type: 'stress', amount: 1 }, buttonLabel: '🛡️ Shrug It Off (1S)', buttonIcon: '🛡️' }),
  domainCard('ground-pound', 'Ground Pound', 'valor', 8, 'Ability',
    'Spend 2H + Strength Roll vs Very Close → 4d10+8 damage + Far knockback.',
    { cost: { type: 'hope', amount: 2 }, buttonLabel: '💥 Ground Pound (2H)', buttonIcon: '💥' }),
];

// ─── DOMAIN_CARD Token-Based SingleAction Configs ────────────────────────────

const DOMAIN_CARD_TOKEN_ACTION_CONFIGS = [
  // ── BLADE Domain ──
  domainCardToken('a-soldiers-bond', "A Soldier's Bond", 'blade', 2, 'Ability',
    'Once/long rest: Compliment someone or ask about their skill, you both gain 3 Hope.',
    'soldiers-bond-charge', 'long-rest',
    { buttonLabel: '🤝 Compliment (+3 Hope Both)', buttonIcon: '🤝' }),
  domainCardToken('scramble', 'Scramble', 'blade', 3, 'Reaction',
    'Once/rest: When Melee enemy deals damage, avoid and move out of range.',
    'scramble-charge', 'rest',
    { buttonLabel: '🏃 Scramble', buttonIcon: '🏃' }),
  domainCardToken('deadly-focus', 'Deadly Focus', 'blade', 4, 'Ability',
    'Once/rest: +1 Proficiency until you attack different target, defeat target, or battle ends.',
    'deadly-focus-charge', 'rest',
    { buttonLabel: '🎯 Focus Target (+1 Prof)', buttonIcon: '🎯',
      sessionStateKey: 'deadlyFocusActive', activeLabel: '+1 Proficiency Active' }),
  domainCardToken('battle-hardened', 'Battle-Hardened', 'blade', 6, 'Ability',
    'Once per long rest: When making Death Move, spend Hope to clear 1 HP instead.',
    'battle-hardened-charge', 'long-rest',
    { cost: { type: 'hope', amount: 1 }, buttonLabel: '💪 Avoid Death (1H)', buttonIcon: '💪' }),

  // ── BONE Domain ──
  domainCardToken('splintering-strike', 'Splintering Strike', 'bone', 9, 'Ability',
    'Once/long rest, 1 Hope: Attack all in weapon range. Distribute damage + bonus die per target.',
    'splintering-strike-charge', 'long-rest',
    { cost: { type: 'hope', amount: 1 }, buttonLabel: '💥 Splintering Strike (1H)', buttonIcon: '💥' }),

  // ── ARCANA Domain ──
  domainCardToken('premonition', 'Premonition', 'arcana', 5, 'Spell',
    'Once/long rest: After GM conveys consequences, rescind move and retry.',
    'premonition-charge', 'long-rest',
    { buttonLabel: '🔮 Undo Consequences', buttonIcon: '🔮' }),

  // ── GRACE Domain ──
  domainCardToken('astral-projection', 'Astral Projection', 'grace', 8, 'Spell',
    "Once/long rest, 1 Stress: Create projection anywhere you've been. See/act through it until rest or damaged.",
    'astral-projection-charge', 'long-rest',
    { cost: { type: 'stress', amount: 1 }, buttonLabel: '👻 Project (1S)', buttonIcon: '👻',
      sessionStateKey: 'astralProjectionActive', activeLabel: 'Projection Active' }),

  // ── MIDNIGHT Domain ──
  domainCardToken('night-terror', 'Night Terror', 'midnight', 9, 'Spell',
    'Once/long rest: Very Close targets Reaction (16) or Horrified+Vulnerable. Steal Fear, deal d6 per Fear stolen.',
    'night-terror-charge', 'long-rest',
    { buttonLabel: '👹 Night Terror', buttonIcon: '👹' }),

  // ── SPLENDOR Domain ──
  domainCardToken('divination', 'Divination', 'splendor', 4, 'Spell',
    'Once per long rest: Spend 3 Hope to ask one yes/no question about the near future.',
    'divination-charge', 'long-rest',
    { cost: { type: 'hope', amount: 3 }, buttonLabel: '🔮 Divination (3H)', buttonIcon: '🔮' }),
  domainCardToken('smite', 'Smite', 'splendor', 5, 'Spell',
    'Once per rest: Spend 3 Hope to charge smite. Next weapon attack = double damage (magic).',
    'smite-charge', 'rest',
    { cost: { type: 'hope', amount: 3 }, buttonLabel: '⚡ Charge Smite (3H)', buttonIcon: '⚡',
      sessionStateKey: 'smiteCharged', activeLabel: '✓ Smite Ready!' }),

  // ── VALOR Domain ──
  domainCardToken('full-surge', 'Full Surge', 'valor', 8, 'Ability',
    'Once/long rest, 3 Stress: +2 to ALL character traits until next rest.',
    'full-surge-charge', 'long-rest',
    { cost: { type: 'stress', amount: 3 }, buttonLabel: '💪 Full Surge (3S)', buttonIcon: '💪',
      sessionStateKey: 'fullSurgeActive', activeLabel: '+2 All Traits Active' }),
];

// ─── DualButton / TripleButton Configs (C6) ────────────────────────────────
// Domain cards with 2-3 distinct action buttons. Each action is independent.
// Deferred: cards using triggersPendingSpellAttack, SimpleTokenService handlers,
// rollDice, requiresPrimary+consumesPrimary (spell routing), or uniqueSelectionsOnly.

const DUAL_TRIPLE_BUTTON_CONFIGS = [
  // ── cruel-precision (BONE L7, Ability) ──
  // On successful weapon attack: Add Finesse OR Agility to damage
  ['cruel-precision', {
    id: 'srd_dc_cruel_precision', version: '1.0.0',
    meta: { name: 'Cruel Precision', summary: 'On successful weapon attack: Add Finesse OR Agility to damage. Choose one!', description: 'On successful weapon attack: Add Finesse OR Agility to damage. Choose one!', source: 'srd', containerContext: 'domain-card', domain: 'bone', level: 7, cardType: 'Ability' },
    actions: [
      { id: 'finesse', label: '🎯 +Finesse', variant: 'primary',
        effects: [{ type: 'show_toast', message: 'Adding Finesse to damage roll!', icon: '🎯' }] },
      { id: 'agility', label: '⚡ +Agility', variant: 'primary',
        effects: [{ type: 'show_toast', message: 'Adding Agility to damage roll!', icon: '⚡' }] },
    ],
  }],

  // ── gore-and-glory (BLADE L9, Triggered) ──
  // On CRIT or defeating enemy: Gain Hope OR clear Stress
  ['gore-and-glory', {
    id: 'srd_dc_gore_and_glory', version: '1.0.0',
    meta: { name: 'Gore and Glory', summary: 'On CRIT or defeating enemy: Gain Hope OR clear Stress. Your choice!', description: 'On CRIT or defeating enemy: Gain Hope OR clear Stress. Your choice!', source: 'srd', containerContext: 'domain-card', domain: 'blade', level: 9, cardType: 'Triggered' },
    actions: [
      { id: 'hope', label: '✨ Gain Hope', variant: 'primary',
        effects: [{ type: 'gain_hope', amount: 1 }, { type: 'show_toast', message: '+1 Hope from glory!', icon: '✨' }] },
      { id: 'stress', label: '💆 Clear Stress', variant: 'primary',
        effects: [{ type: 'clear_stress', amount: 1 }, { type: 'show_toast', message: 'Cleared 1 Stress!', icon: '💆' }] },
    ],
  }],

  // ── rage-up (BLADE L6, Ability) ──
  // Before attack: Mark 1-2 Stress for +2× Strength per Stress to damage
  ['rage-up', {
    id: 'srd_dc_rage_up', version: '1.0.0',
    meta: { name: 'Rage Up', summary: 'Before attack: Mark 1-2 Stress for +2× Strength per Stress to damage!', description: 'Before attack: Mark 1-2 Stress for +2× Strength per Stress to damage!', source: 'srd', containerContext: 'domain-card', domain: 'blade', level: 6, cardType: 'Ability' },
    actions: [
      { id: 'single', label: '💢 Rage (1S)', variant: 'warning',
        conditions: [{ type: 'resource_available', resource: 'stress', amount: 1 }],
        costs: [{ type: 'mark_stress', amount: 1 }],
        effects: [{ type: 'show_toast', message: '+2× Strength to damage!', icon: '💢' }],
        labels: { disabled: 'No Stress' } },
      { id: 'double', label: '🔥 Double (2S)', variant: 'warning',
        conditions: [{ type: 'resource_available', resource: 'stress', amount: 2 }],
        costs: [{ type: 'mark_stress', amount: 2 }],
        effects: [{ type: 'show_toast', message: '+4× Strength to damage!', icon: '🔥' }],
        labels: { disabled: 'Need 2 Stress' } },
    ],
  }],

  // ── bold-presence (VALOR L2, Ability) ──
  // Spend Hope to add Str to Presence. Once per rest: avoid gaining a condition.
  ['bold-presence', {
    id: 'srd_dc_bold_presence', version: '1.0.0',
    meta: { name: 'Bold Presence', summary: 'Spend Hope to add Str to Presence. Once per rest: avoid gaining a condition.', description: 'Spend Hope to add Str to Presence. Once per rest: avoid gaining a condition.', source: 'srd', containerContext: 'domain-card', domain: 'valor', level: 2, cardType: 'Ability' },
    actions: [
      { id: 'presenceBoost', label: '💪 +Str to Presence (1H)', variant: 'primary',
        conditions: [{ type: 'resource_available', resource: 'hope', amount: 1 }],
        costs: [{ type: 'spend_hope', amount: 1 }],
        effects: [
          { type: 'set_session_state', key: 'boldPresenceActive', value: true },
          { type: 'show_toast', message: 'Adding Strength to Presence Roll!', icon: '💪' },
        ],
        labels: { disabled: 'Need 1 Hope' } },
      { id: 'avoidCondition', label: '🛡️ Avoid Condition', variant: 'primary',
        conditions: [{ type: 'has_token', tokenId: 'bold-presence-avoid' }],
        costs: [{ type: 'spend_token', tokenId: 'bold-presence-avoid', amount: 1 }],
        effects: [{ type: 'show_toast', message: 'Bold presence avoids condition!', icon: '🛡️' }],
        labels: { disabled: 'Used This Rest' } },
    ],
  }],

  // ── phantom-retreat (MIDNIGHT L5, Spell) ──
  // Two-phase: Mark location (1H), then teleport back (1H)
  ['phantom-retreat', {
    id: 'srd_dc_phantom_retreat', version: '1.0.0',
    meta: { name: 'Phantom Retreat', summary: 'Mark location (1H), then teleport back before rest (1H). Ends after reappearing.', description: 'Mark location (1H), then teleport back before rest (1H). Ends after reappearing.', source: 'srd', containerContext: 'domain-card', domain: 'midnight', level: 5, cardType: 'Spell' },
    actions: [
      { id: 'markLocation', label: '📍 Mark Location (1H)', variant: 'primary',
        conditions: [
          { type: 'resource_available', resource: 'hope', amount: 1 },
          { type: 'not_active', stateKey: 'phantomRetreatLocation' },
        ],
        costs: [{ type: 'spend_hope', amount: 1 }],
        effects: [
          { type: 'set_session_state', key: 'phantomRetreatLocation', value: true },
          { type: 'show_toast', message: 'Location marked for phantom retreat!', icon: '📍' },
        ],
        labels: { disabled: 'Need 1 Hope', active: '📍 Location Marked' } },
      { id: 'teleportBack', label: '👻 Retreat (1H)', variant: 'secondary',
        conditions: [
          { type: 'resource_available', resource: 'hope', amount: 1 },
          { type: 'is_active', stateKey: 'phantomRetreatLocation' },
        ],
        costs: [{ type: 'spend_hope', amount: 1 }],
        effects: [
          { type: 'clear_session_state', key: 'phantomRetreatLocation' },
          { type: 'show_toast', message: 'Teleported back to marked location!', icon: '👻' },
        ],
        labels: { disabled: 'Mark location first' } },
    ],
  }],

  // ── natural-familiar (SAGE L2, Spell) ──
  // Summon familiar (1H) or flying (2H). +d6 damage in its Melee range.
  ['natural-familiar', {
    id: 'srd_dc_natural_familiar', version: '1.0.0',
    meta: { name: 'Natural Familiar', summary: 'Summon familiar (1H) or flying (2H). Mark Stress to see through eyes. +d6 damage in its Melee.', description: 'Summon familiar (1H) or flying (2H). Mark Stress to see through eyes. +d6 damage in its Melee.', source: 'srd', containerContext: 'domain-card', domain: 'sage', level: 2, cardType: 'Spell' },
    actions: [
      { id: 'summon', label: '🐿️ Summon (1H)', variant: 'primary',
        conditions: [
          { type: 'resource_available', resource: 'hope', amount: 1 },
          { type: 'not_active', stateKey: 'naturalFamiliarActive' },
        ],
        costs: [{ type: 'spend_hope', amount: 1 }],
        effects: [
          { type: 'set_session_state', key: 'naturalFamiliarActive', value: true },
          { type: 'show_toast', message: 'Familiar summoned! +d6 damage in its Melee range.', icon: '🐿️' },
        ],
        labels: { disabled: 'Need 1 Hope', active: '🐿️ Familiar Active' } },
      { id: 'flying', label: '🦉 Flying (2H)', variant: 'primary',
        conditions: [
          { type: 'resource_available', resource: 'hope', amount: 2 },
          { type: 'not_active', stateKey: 'naturalFamiliarActive' },
        ],
        costs: [{ type: 'spend_hope', amount: 2 }],
        effects: [
          { type: 'set_session_state', key: 'naturalFamiliarActive', value: true },
          { type: 'show_toast', message: 'Flying familiar summoned! +d6 damage in its Melee range.', icon: '🦉' },
        ],
        labels: { disabled: 'Need 2 Hope', active: '🦉 Familiar Active' } },
    ],
  }],

  // ── know-thy-enemy (BONE L5, Ability) ──
  // Instinct Roll vs target. On success, spend Hope to learn info.
  ['know-thy-enemy', {
    id: 'srd_dc_know_thy_enemy', version: '1.0.0',
    meta: { name: 'Know Thy Enemy', summary: 'Instinct Roll vs target. On success, spend Hope to learn: HP/Stress OR Difficulty/Thresholds.', description: 'Instinct Roll vs target. On success, spend Hope to learn: HP/Stress OR Difficulty/Thresholds.', source: 'srd', containerContext: 'domain-card', domain: 'bone', level: 5, cardType: 'Ability' },
    actions: [
      { id: 'hpStress', label: '💔 HP/Stress (1H)', variant: 'primary',
        conditions: [{ type: 'resource_available', resource: 'hope', amount: 1 }],
        costs: [{ type: 'spend_hope', amount: 1 }],
        effects: [{ type: 'show_toast', message: "Ask GM for target's unmarked HP and Stress.", icon: '💔' }],
        labels: { disabled: 'Need 1 Hope' } },
      { id: 'diffThresh', label: '🎯 Diff/Thresh (1H)', variant: 'primary',
        conditions: [{ type: 'resource_available', resource: 'hope', amount: 1 }],
        costs: [{ type: 'spend_hope', amount: 1 }],
        effects: [{ type: 'show_toast', message: "Ask GM for target's Difficulty and damage thresholds.", icon: '🎯' }],
        labels: { disabled: 'Need 1 Hope' } },
    ],
  }],

  // ── forceful-push (VALOR L1, Ability) — TRIPLE_BUTTON ──
  // Melee attack + knockback. Optional: Spend Hope for +d6 and/or Vulnerable.
  ['forceful-push', {
    id: 'srd_dc_forceful_push', version: '1.0.0',
    meta: { name: 'Forceful Push', summary: 'Melee attack + knockback. Optional: Spend Hope for +d6 and/or Vulnerable.', description: 'Melee attack + knockback. Optional: Spend Hope for +d6 and/or Vulnerable.', source: 'srd', containerContext: 'domain-card', domain: 'valor', level: 1, cardType: 'Ability' },
    actions: [
      { id: 'attack', label: '🗡️ Push', variant: 'primary',
        effects: [{ type: 'show_toast', message: 'Melee attack + knockback to Close range!', icon: '🗡️' }] },
      { id: 'bonusDamage', label: '🎲 +d6 (1H)', variant: 'primary',
        conditions: [{ type: 'resource_available', resource: 'hope', amount: 1 }],
        costs: [{ type: 'spend_hope', amount: 1 }],
        effects: [{ type: 'show_toast', message: '+d6 added to damage on success with Hope!', icon: '🎲' }],
        labels: { disabled: 'Need 1 Hope' } },
      { id: 'vulnerable', label: '💢 Vuln (1H)', variant: 'primary',
        conditions: [{ type: 'resource_available', resource: 'hope', amount: 1 }],
        costs: [{ type: 'spend_hope', amount: 1 }],
        effects: [{ type: 'show_toast', message: 'Target is Vulnerable!', icon: '💢' }],
        labels: { disabled: 'Need 1 Hope' } },
    ],
  }],
];

// ─── Remaining Tier 0-1 Configs ─────────────────────────────────────────────

const REMAINING_TIER01_CONFIGS = [
  // ── Domain Card: through-your-eyes (GRACE L4 Spell) — session state toggle ──
  ['through-your-eyes', {
    id: 'srd_dc_through_your_eyes', version: '1.0.0',
    meta: {
      name: 'Through Your Eyes',
      summary: 'Target in Very Far range. See/hear through their senses until you cast another spell or rest.',
      description: 'Target in Very Far range. See/hear through their senses until you cast another spell or rest.',
      source: 'srd', containerContext: 'domain-card', domain: 'grace', level: 4, cardType: 'Spell',
    },
    actions: [{
      id: 'activate',
      label: '👁️ Link Senses',
      variant: 'primary',
      effects: [
        { type: 'set_session_state', key: 'throughYourEyesActive', value: true },
        { type: 'show_toast', message: 'Senses linked! See and hear through target.', icon: '👁️' },
      ],
      toggleOff: {
        label: '👁️ Linked (End)',
        variant: 'activeSuccess',
        effects: [
          { type: 'clear_session_state', key: 'throughYourEyesActive' },
          { type: 'show_toast', message: 'Through Your Eyes ended.', icon: '👁️' },
        ],
      },
    }],
  }],

  // ── Ability: Rousing Speech (Wordsmith) — once/rest token spend ──
  ['ability:subclass:Wordsmith:Rousing Speech', {
    id: 'srd_wordsmith_rousing_speech', version: '1.0.0',
    meta: {
      name: 'Rousing Speech',
      summary: 'Once/rest: All allies in Far range clear 2 Stress',
      source: 'srd', containerContext: 'ability', containerType: 'subclass',
    },
    actions: [{
      id: 'activate',
      label: '🎤 Give Speech',
      variant: 'primary',
      conditions: [{ type: 'has_token', tokenId: 'rousing-speech', resourceKey: 'subclass-wordsmith' }],
      costs: [{ type: 'spend_token', tokenId: 'rousing-speech', resourceKey: 'subclass-wordsmith', amount: 1 }],
      effects: [
        { type: 'show_toast', message: 'Rousing Speech! All allies in Far range clear 2 Stress.', icon: '🎤' },
      ],
      labels: { disabled: 'Speech Given' },
    }],
  }],

  // ── Ability: Battle Ritual (Call of the Brave) — once/rest, clear stress + gain hope ──
  ['ability:subclass:Call of the Brave:Battle Ritual', {
    id: 'srd_call_of_the_brave_battle_ritual', version: '1.0.0',
    meta: {
      name: 'Battle Ritual',
      summary: 'Once/rest: Before danger, clear 2 Stress and gain 2 Hope',
      source: 'srd', containerContext: 'ability', containerType: 'subclass',
    },
    actions: [{
      id: 'activate',
      label: '⚔️ Battle Ritual',
      variant: 'primary',
      conditions: [{ type: 'has_token', tokenId: 'battle-ritual', resourceKey: 'subclass-call-of-the-brave' }],
      costs: [{ type: 'spend_token', tokenId: 'battle-ritual', resourceKey: 'subclass-call-of-the-brave', amount: 1 }],
      effects: [
        { type: 'clear_stress', amount: 2 },
        { type: 'gain_hope', amount: 2 },
        { type: 'show_toast', message: 'Battle Ritual complete! Cleared 2 Stress, gained 2 Hope.', icon: '⚔️' },
      ],
      labels: { disabled: 'Ritual Complete' },
    }],
  }],

  // ── Ability: Sparing Touch (Divine Wielder) — once/rest, heal 2 HP or clear 2 Stress ──
  ['ability:subclass:Divine Wielder:Sparing Touch', {
    id: 'srd_divine_wielder_sparing_touch', version: '1.0.0',
    meta: {
      name: 'Sparing Touch',
      summary: 'Once/rest: Touch creature, clear 2 HP or 2 Stress',
      source: 'srd', containerContext: 'ability', containerType: 'subclass',
    },
    actions: [
      {
        id: 'heal-hp',
        label: '🙏 Heal 2 HP',
        variant: 'primary',
        conditions: [{ type: 'has_token', tokenId: 'sparing-touch', resourceKey: 'subclass-divine-wielder' }],
        costs: [{ type: 'spend_token', tokenId: 'sparing-touch', resourceKey: 'subclass-divine-wielder', amount: 1 }],
        effects: [
          { type: 'heal_hp', amount: 2 },
          { type: 'show_toast', message: 'Sparing Touch! Cleared 2 HP.', icon: '🙏' },
        ],
        labels: { disabled: 'Touch Used' },
      },
      {
        id: 'clear-stress',
        label: '🙏 Clear 2 Stress',
        variant: 'primary',
        conditions: [{ type: 'has_token', tokenId: 'sparing-touch', resourceKey: 'subclass-divine-wielder' }],
        costs: [{ type: 'spend_token', tokenId: 'sparing-touch', resourceKey: 'subclass-divine-wielder', amount: 1 }],
        effects: [
          { type: 'clear_stress', amount: 2 },
          { type: 'show_toast', message: 'Sparing Touch! Cleared 2 Stress.', icon: '🙏' },
        ],
        labels: { disabled: 'Touch Used' },
      },
    ],
  }],
];

// ─── Aggregate ──────────────────────────────────────────────────────────────

const SRD_COMPOSED_MECHANICS = [
  ...SUMMARY_ONLY_CONFIGS,
  ...ACTIVATION_TOGGLE_CONFIGS,
  ...HOPE_SPEND_CONFIGS,
  ...STRESS_FOR_BONUS_CONFIGS,
  ...DOMAIN_CARD_SINGLE_ACTION_CONFIGS,
  ...DOMAIN_CARD_TOKEN_ACTION_CONFIGS,
  ...DUAL_TRIPLE_BUTTON_CONFIGS,
  ...REMAINING_TIER01_CONFIGS,
];

/**
 * Register all SRD composed mechanics in the registry.
 * Called once at app startup from index.js.
 */
export const registerSRDComposedMechanics = () => {
  if (SRD_COMPOSED_MECHANICS.length > 0) {
    registerComposedMechanics(SRD_COMPOSED_MECHANICS);
  }
};
